import React, { useEffect, useState } from 'react';

import { BuilderBlocks } from '@builder.io/react';
import PropTypes from 'prop-types';
import styled from 'styled-components';

import WhitelabelIcon from '~/icons/components/WhitelabelIcon';
import BuilderLocalizedText from '~/shared/components/BuilderLocalizedText';
import DelayUnmountWrapper from '~/shared/components/DelayUnmountWrapper';
import { getBrandValue } from '~/shared/utils/brandNameHelper';
import { createDataAutoTagString } from '~/shared/utils/createDataAutoTagString';
import { getBuilderDataAnalyticsProps } from '~/shared/utils/getBuilderDataAnalyticsProps';
import {
  ExpansionPanel,
  FABLETICS_BUILDER_ENUMS,
} from '~/techstyle-shared/react-builder-models';

const NAV_CONTENT_COLORS = FABLETICS_BUILDER_ENUMS.NAV_CONTENT_COLORS;
const EXPANSION_PANEL_LEVELS = ExpansionPanel.EXPANSION_PANEL_LEVELS;

const TRANSITION_DURATION = 300;

const ExpansionPanelWrapper = styled.ul`
  list-style: none;
  ${({ $level, theme }) =>
    $level === EXPANSION_PANEL_LEVELS.FOOTER &&
    theme.expansionPanel.panelWrapper.footer}
`;

const PanelHeader = styled.div`
  display: flex;
  justify-content: space-between;
  cursor: pointer;

  ${({ $level, theme }) =>
    $level === EXPANSION_PANEL_LEVELS.PRIMARY &&
    theme.expansionPanel.panelHeader.primary}

  ${({ $level, theme }) =>
    $level === EXPANSION_PANEL_LEVELS.SECONDARY &&
    theme.expansionPanel.panelHeader.secondary}

  ${({ $level, theme }) =>
    $level === EXPANSION_PANEL_LEVELS.FOOTER &&
    theme.expansionPanel.panelHeader.footer}
`;

const PanelLabel = styled.p`
  ${({ $level, theme }) =>
    $level === EXPANSION_PANEL_LEVELS.PRIMARY &&
    theme.expansionPanel.panelLabel.primary}

  ${({ $level, theme }) =>
    $level === EXPANSION_PANEL_LEVELS.SECONDARY &&
    theme.expansionPanel.panelLabel.secondary}

  ${({ $level, theme }) =>
    $level === EXPANSION_PANEL_LEVELS.FOOTER &&
    theme.expansionPanel.panelLabel.footer}

  ${({ $customLabelColor, theme }) =>
    $customLabelColor &&
    $customLabelColor !== NAV_CONTENT_COLORS.DEFAULT &&
    theme.builderHeader.customColors[$customLabelColor]};
`;

const PanelIcon = styled.span`
  transition: transform ${TRANSITION_DURATION}ms ease;
  transform: ${({ $isExpanded }) => ($isExpanded ? 'rotate(180deg)' : 'none')};

  ${({ theme }) => theme.expansionPanel.panelIcon}

  & > * {
    vertical-align: middle;
  }
`;

const AnimationContainer = styled.div`
  transition: max-height ${TRANSITION_DURATION}ms ease,
    opacity ${TRANSITION_DURATION}ms ease;
  max-height: ${({ $isExpanded }) => ($isExpanded ? '3000px' : '0')};
  opacity: ${({ $isExpanded }) => ($isExpanded ? '1' : '0')};
`;

const ExpansionPanelContext = React.createContext();

function Panel({ panelData, panelLevel, index, builderBlockId }) {
  const {
    panelLabel,
    panelContent,
    panelLabelColor,
    expandPanelByDefault,
    dataAnalytics,
  } = panelData;

  const [isExpanded, setIsExpanded] = useState(expandPanelByDefault || false);
  const [animateExpanded, setAnimateExpanded] = useState(false);
  const { isParentPanelExpanded } =
    React.useContext(ExpansionPanelContext) || {};

  const handleKeyPress = (event) => {
    if (event.key === 'Enter') {
      setIsExpanded((isExpanded) => !isExpanded);
    }
  };

  useEffect(() => {
    // Delay the animation to allow the panel to expand first
    if (isExpanded) {
      setTimeout(() => {
        setAnimateExpanded(true);
      }, 50);
    } else {
      setAnimateExpanded(false);
    }
  }, [isExpanded]);

  useEffect(() => {
    const isPrimaryOrFooter = [
      EXPANSION_PANEL_LEVELS.PRIMARY,
      EXPANSION_PANEL_LEVELS.FOOTER,
    ].includes(panelLevel);

    if (isPrimaryOrFooter && expandPanelByDefault) {
      setIsExpanded(true);
    } else if (!isPrimaryOrFooter) {
      setIsExpanded(isParentPanelExpanded && expandPanelByDefault);
    }
  }, [expandPanelByDefault, isParentPanelExpanded, panelLevel]);

  const dataAutotag = dataAnalytics?.label
    ? createDataAutoTagString(dataAnalytics.label)
    : createDataAutoTagString(panelLevel, 'panel', panelLabel);

  const chevronDownSize = getBrandValue({
    savage: 24,
    default: '1em',
  });

  return (
    <li>
      <PanelHeader
        $level={panelLevel}
        onClick={() => setIsExpanded((isExpanded) => !isExpanded)}
        onKeyUp={handleKeyPress}
        aria-expanded={isExpanded}
        role="button"
        tabIndex="0"
        data-autotag={dataAutotag}
        data-label={panelLabel}
        {...getBuilderDataAnalyticsProps(dataAnalytics)}
      >
        <PanelLabel $level={panelLevel} $customLabelColor={panelLabelColor}>
          <BuilderLocalizedText text={panelLabel} />
        </PanelLabel>

        <PanelIcon $isExpanded={isExpanded}>
          <WhitelabelIcon iconName="chevronDown" size={chevronDownSize} />
        </PanelIcon>
      </PanelHeader>

      <DelayUnmountWrapper
        $level={panelLevel}
        isMounted={isExpanded}
        delayTime={TRANSITION_DURATION}
        toggleDisplayOnly
      >
        <AnimationContainer $isExpanded={animateExpanded}>
          <ExpansionPanelContext.Provider
            value={{ isParentPanelExpanded: isExpanded }}
          >
            <BuilderBlocks
              parentElementId={builderBlockId}
              dataPath={`component.options.panels.${index}.panelContent`}
              blocks={panelContent}
            />
          </ExpansionPanelContext.Provider>
        </AnimationContainer>
      </DelayUnmountWrapper>
    </li>
  );
}

Panel.propTypes = {
  builderBlockId: PropTypes.string,
  index: PropTypes.number,
  panelData: PropTypes.object,
  panelLevel: PropTypes.string,
};

function BuilderExpansionPanel({ expansionPanelLevel, panels, builderBlock }) {
  return (
    <ExpansionPanelWrapper
      $level={expansionPanelLevel}
      data-builder-component-type="expansion_panel"
    >
      {panels.map((panelData, index) => {
        return (
          <Panel
            key={index}
            index={index}
            panelData={panelData}
            panelLevel={expansionPanelLevel}
            builderBlockId={builderBlock.id}
          />
        );
      })}
    </ExpansionPanelWrapper>
  );
}

BuilderExpansionPanel.propTypes = {
  builderBlock: PropTypes.object,
  expansionPanelLevel: PropTypes.string,
  panels: PropTypes.oneOfType([PropTypes.object, PropTypes.array]),
};

export default BuilderExpansionPanel;
